import { SessionExpired } from './api.ts'

/** What became of a batch: what did not go through, and the first reason given. */
export interface BatchReverify {
  /** The ids that were refused, in the order they were asked. */
  readonly failed: readonly string[]
  /** The first refusal's message, which is the one worth showing. */
  readonly reason: string | undefined
}

/**
 * Asks for a reverify per id, and finishes the list whatever any one of them
 * answers.
 *
 * This was a `for` loop with one `await` inside a single `try`, which meant the
 * first refusal threw out of the loop and every remaining selection was
 * silently dropped: one generic toast, the selection untouched, and no way to
 * tell which rows had actually been asked. Selecting a page of mixed rows
 * whose first id happened to be `released` did nothing at all, which is what
 * "o Atualizar não funciona" was.
 *
 * Sequential on purpose, as it always was. Each of these starts model work,
 * and firing fifty at once would hand the worker the spike that admission
 * exists to prevent.
 *
 * A lapsed session is the one thing that does stop it: the app is already
 * moving to the login screen, and the remaining ids would each buy another 401.
 * Those are reported as failed, because they were not asked.
 */
export async function reverifyEach(
  ids: Iterable<string>,
  ask: (id: string) => Promise<unknown>,
): Promise<BatchReverify> {
  const failed: string[] = []
  let reason: string | undefined
  const remaining = [...ids]

  while (remaining.length > 0) {
    const id = remaining.shift()!
    try {
      await ask(id)
    } catch (cause) {
      failed.push(id)
      reason ??= cause instanceof Error ? cause.message : 'falha ao atualizar'
      if (cause instanceof SessionExpired) {
        failed.push(...remaining)
        break
      }
    }
  }

  return { failed, reason }
}
